/* AF - sheet controller for <dialog class="af-sheet"> (assets/af-ui.css).
   Phone: bottom sheet. Desktop: centred window. Shared by the product page
   now and by the cart drawer / quick add / filters in the later passes.

   Markup:
     <button data-af-sheet-open="sheet-id">…</button>
     <dialog class="af-sheet" id="sheet-id" aria-labelledby="…">
       <div class="af-sheet__scrim" data-af-sheet-close></div>
       <div class="af-sheet__panel">… <button data-af-sheet-close>…</button> …</div>
     </dialog>

   Behaviour:
   - showModal(): the sheet sits in the top layer, the page behind is inert,
     Tab stays inside. Then .is-open starts the slide/fade.
   - Closing (X, scrim tap, Escape, phone back button) plays the exit, then
     close(); focus returns to whatever opened it.
   - Back button: opening pushes one history entry with no URL change (no
     extra page_view in analytics); back pops it and closes the sheet.
     Closing any other way steps back over that entry.
   - The page behind doesn't scroll while a sheet is open.
   API: window.AFSheet.open(idOrEl, opener?), .close(idOrEl?), .isOpen() */
(() => {
  if (window.AFSheet) return;

  const TOKEN = `af_sheet_${Math.random().toString(36).slice(2)}`;
  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const byId = (x) => (typeof x === 'string' ? document.getElementById(x) : x);

  let current = null;     // the open dialog
  let opener = null;      // element to refocus on close
  let pushed = false;     // our history entry is on the stack
  let closing = false;

  /* ---------- scroll lock (keeps the scrollbar gutter so nothing shifts) ---------- */
  let savedPad = '';
  function lock() {
    const html = document.documentElement;
    const gutter = window.innerWidth - html.clientWidth;
    savedPad = document.body.style.paddingRight;
    if (gutter > 0) document.body.style.paddingRight = `${gutter}px`;
    html.classList.add('af-sheet-lock');
  }
  function unlock() {
    document.documentElement.classList.remove('af-sheet-lock');
    document.body.style.paddingRight = savedPad;
  }

  function finishClose(dlg) {
    dlg.classList.remove('is-open');
    if (dlg.open) dlg.close();
    unlock();
    closing = false;
    if (current === dlg) current = null;
    const back = opener;
    opener = null;
    if (back && back.isConnected && typeof back.focus === 'function') back.focus({ preventScroll: true });
    dlg.dispatchEvent(new CustomEvent('af-sheet:closed', { bubbles: true }));
  }

  // Exit animation, then really close. `instant` skips the animation
  // (bfcache restore, or reduced motion).
  function hide(dlg, instant) {
    if (!dlg || !dlg.open || closing) return;
    closing = true;
    if (instant || reduceMotion()) { finishClose(dlg); return; }
    const panel = dlg.querySelector('.af-sheet__panel');
    let done = false;
    const end = () => { if (done) return; done = true; finishClose(dlg); };
    if (panel) panel.addEventListener('transitionend', (e) => { if (e.target === panel && e.propertyName === 'transform') end(); });
    setTimeout(end, 450);
    dlg.classList.remove('is-open');
  }

  function open(target, from) {
    const dlg = byId(target);
    if (!dlg || typeof dlg.showModal !== 'function') return false;
    if (current === dlg && dlg.open) return true;
    if (current && current !== dlg) hide(current, true);
    opener = from || document.activeElement;
    current = dlg;
    closing = false;
    lock();
    dlg.showModal();
    // showModal() scrolls the dialog to its first focusable control, which
    // sits off-screen until the slide runs. The CSS uses overflow:clip so it
    // can't scroll; this undoes it on browsers without clip (Safari < 16).
    dlg.scrollTop = 0;
    dlg.scrollLeft = 0;
    // Focus the panel, not the close button: a ring on "X" as the sheet
    // appears reads as a mistake, and screen readers still start at the top.
    const panel = dlg.querySelector('.af-sheet__panel');
    if (panel) {
      if (!panel.hasAttribute('tabindex')) panel.setAttribute('tabindex', '-1');
      panel.focus({ preventScroll: true });
    }
    const body = dlg.querySelector('.af-sheet__body');
    if (body) body.scrollTop = 0;
    // two frames: the closed position paints first, so the slide plays
    requestAnimationFrame(() => requestAnimationFrame(() => dlg.classList.add('is-open')));
    // switching straight from one sheet to another reuses the same entry
    if (!(pushed && ours())) {
      try {
        history.pushState({ __afSheet: TOKEN }, '');
        pushed = true;
      } catch (e) {
        pushed = false;
      }
    }
    dlg.dispatchEvent(new CustomEvent('af-sheet:opened', { bubbles: true }));
    return true;
  }

  function ours() {
    try { return !!(history.state && history.state.__afSheet === TOKEN); } catch (e) { return false; }
  }

  // Every close goes through here. If our history entry is on top, step back
  // over it and let popstate do the closing, so back and X behave the same.
  function close(target) {
    const dlg = byId(target) || current;
    if (!dlg || !dlg.open) return;
    if (pushed && ours()) {
      pushed = false;
      // a size picked inside the sheet changed this entry's URL (?variant=);
      // keep it on the entry we step back to
      carryHref = location.href;
      history.back();
      return; // popstate closes it
    }
    pushed = false;
    hide(dlg);
  }

  let carryHref = '';
  window.addEventListener('popstate', () => {
    const href = carryHref;
    carryHref = '';
    if (!current) return;
    if (ours()) return; // forward onto our own entry; nothing to do
    pushed = false;
    if (href && href !== location.href) {
      try { history.replaceState(history.state, '', href); } catch (e) {}
    }
    hide(current);
  });

  // bfcache restore with a sheet open: close it at once, release the lock
  window.addEventListener('pageshow', (e) => {
    if (e.persisted && current) { pushed = false; hide(current, true); }
  });

  document.addEventListener('click', (e) => {
    const t = e.target;
    if (!t || !t.closest) return;
    const openBtn = t.closest('[data-af-sheet-open]');
    if (openBtn) {
      const id = openBtn.getAttribute('data-af-sheet-open');
      if (open(id, openBtn)) e.preventDefault();
      return;
    }
    if (t.closest('[data-af-sheet-close]') && t.closest('dialog.af-sheet')) {
      e.preventDefault();
      close(t.closest('dialog.af-sheet'));
    }
  });

  // Escape fires `cancel`; take it over so the exit animation plays
  document.addEventListener('cancel', (e) => {
    if (e.target instanceof HTMLDialogElement && e.target.classList.contains('af-sheet')) {
      e.preventDefault();
      close(e.target);
    }
  }, true);

  window.AFSheet = { open, close, isOpen: () => !!(current && current.open) };
})();
